import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, distinct, last, map, mergeMap, Observable, of, withLatestFrom } from 'rxjs';
import { client } from '../..';
import * as actions from './actions';
import { CrossTenantAccessPolicyDefault } from './actions';
import { retry } from 'projects/angular-clarity/src/app/pipes/retry.pipe';
import { TenantAjaxService } from 'projects/angular-clarity/src/app/services/ajax/tenant-ajax.service';

@Injectable()
export class GraphCrossTenantAccessPolicyEffects {
    loadGraphCrossTenantAccessPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadCrossTenantAccessPolicy),
            distinct((action) => action._tenant),
            mergeMap(({ _tenant }) =>
                this.get(_tenant).pipe(
                    retry(5000, 3, 'graph timeout'),
                    last(),
                    map((policy: CrossTenantAccessPolicyDefault | null) =>
                        actions.loadCrossTenantAccessPolicySuccess({ _tenant, policy }),
                    ),
                    catchError((error) => of(actions.loadCrossTenantAccessPolicyFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    updateGraphCrossTenantAccessPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.updateCrossTenantAccessPolicy),
            mergeMap(({ _tenant, policy }) =>
                this.patch(_tenant, policy).pipe(
                    withLatestFrom(this.store.select(client(_tenant).graph.crossTenantAccessPolicy.data)),
                    map(([, current]) => {
                        // Reflect the values we just successfully wrote rather than re-fetching.
                        // The cross-tenant access default is eventually consistent (and, on a tenant
                        // where it was just materialised, an immediate GET can still 404), so a GET
                        // right after the PATCH frequently returns stale/empty data — leaving the
                        // store (and UI) showing the pre-remediation state until a page reload.
                        // Merging the patched fields into the cached policy updates the store
                        // synchronously and accurately.
                        const merged: CrossTenantAccessPolicyDefault = { ...current, ...policy };
                        return actions.updateCrossTenantAccessPolicySuccess({ _tenant, policy: merged });
                    }),
                    catchError((error) => of(actions.updateCrossTenantAccessPolicyFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    private get(tenant: string): Observable<CrossTenantAccessPolicyDefault | null> {
        return this.ajax
            .get<CrossTenantAccessPolicyDefault>(
                tenant,
                '/api/microsoft/graph/policies/crossTenantAccessPolicy/default',
            )
            .pipe(
                catchError((error) => {
                    // Tenants that have never customized their cross-tenant access settings return
                    // 404 Request_ResourceNotFound for the default configuration — Microsoft only
                    // materialises the object on first write. Treat that as "using the Microsoft
                    // service default" so the baseline loads cleanly instead of erroring; the first
                    // remediation PATCH materialises it and subsequent reads return 200.
                    if (error?.status === 404) {
                        return of(null);
                    }
                    throw error;
                }),
            );
    }

    private patch(tenant: string, policy: Partial<CrossTenantAccessPolicyDefault>): Observable<unknown> {
        return this.ajax.patch(tenant, '/api/microsoft/graph/policies/crossTenantAccessPolicy/default', policy);
    }

    constructor(
        private actions$: Actions,
        private ajax: TenantAjaxService,
        private store: Store,
    ) {}
}
